import React, { useState, useMemo, useEffect } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { trackEvent } from 'ddc-track-event';
import { useRequestData } from 'wsm-common-data';
import { useFetchVehicleData } from '../hooks/useFetchVehicleData';
import {
	options,
	SMALL_SCREEN_SIZE,
	MOBILE_SCREEN_SIZE,
	MY_CARS_KEY
} from '../global-constants';
import FacetButton from './FacetButton';
import useLocalStorage from '../hooks/useLocalStorage';

const initialLocalStorageValue = {
	accountId: '',
	newTrackedPriceCount: 0,
	newSaveCount: 0,
	vehicles: []
};

const validParams = Object.values(options);

const getActivityData = (vehicles) => {
	let recentlyViewedCount = 0;
	let trackingPriceCount = 0;
	let savedCount = 0;

	if (Array.isArray(vehicles) && vehicles.length > 0) {
		recentlyViewedCount =
			vehicles?.filter(
				(vehicle) =>
					vehicle[options.RECENTLY_VIEWED] !== '' &&
					vehicle[options.RECENTLY_VIEWED]
			)?.length ?? 0;

		trackingPriceCount =
			vehicles?.filter(
				(vehicle) =>
					vehicle[options.TRACKED_PRICE] !== '' &&
					vehicle[options.TRACKED_PRICE]
			)?.length ?? 0;

		savedCount =
			vehicles?.filter(
				(vehicle) =>
					vehicle[options.SAVED] !== '' && vehicle[options.SAVED]
			)?.length ?? 0;
	}

	const activityData = [
		{
			id: options.RECENTLY_VIEWED,
			count: recentlyViewedCount,
			label: 'RECENTLY_VIEWED'
		},
		{
			id: options.TRACKED_PRICE,
			count: trackingPriceCount,
			label: 'TRACKING_PRICE'
		},
		{
			id: options.SAVED,
			count: savedCount,
			label: 'SAVED'
		}
	];

	return activityData;
};

const trackingObjContainer = [
	{
		id: options.RECENTLY_VIEWED,
		element: 'Recently viewed filter',
		result: 'User filters to recently viewed inventory'
	},
	{
		id: options.TRACKED_PRICE,
		element: 'Tracking price filter',
		result: 'User filters to tracking price inventory'
	},
	{
		id: options.SAVED,
		element: 'Saved filter',
		result: 'User filters to saved inventory'
	}
];

const Options = ({ hookProps }) => {
	const { windowId, widgetName } = useRequestData();
	const [facetValue, setValue] = useState(() => {
		if (isBrowser && window) {
			const params = new URLSearchParams(window.location.search);
			const urlParam = params.get('select');
			const isValidParam = validParams.includes(urlParam);
			return isValidParam ? urlParam : options.RECENTLY_VIEWED;
		}
		return options.RECENTLY_VIEWED;
	});
	const [isSmallScreen, setIsSmallScreen] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	const { storedState: myCarsData, setStateAndLocalStorage } =
		useLocalStorage(MY_CARS_KEY, initialLocalStorageValue);

	const wrapperClasses = useMemo(
		() => [
			'px-4 px-md-5 px-lg-6',
			isSmallScreen || isMobile
				? 'facet-buttons radio-facet-mobile bg-page-title'
				: ''
		],
		[isSmallScreen, isMobile]
	);

	const facetsListClasses = useMemo(
		() => [
			isSmallScreen ? 'd-inline-flex flex-wrap row' : '',
			isMobile ? 'mb-0' : '',
			'facet-list list-unstyled line-height-condensed'
		],
		[isSmallScreen, isMobile]
	);

	const facetClasses = useMemo(
		() => [
			isSmallScreen ? 'mr-4' : 'mb-lg-4',
			isMobile ? 'col-xs-6' : 'mb-lg-4',
			'radio'
		],
		[isMobile, isSmallScreen]
	);

	useFetchVehicleData(
		hookProps,
		facetValue,
		setStateAndLocalStorage,
		myCarsData
	);

	const activityData = useMemo(() => {
		return getActivityData(myCarsData?.vehicles);
	}, [myCarsData]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.has('select')) {
			params.set('select', facetValue);
		} else {
			params.append('select', facetValue);
		}
		window.history.pushState(null, '', `?${params.toString()}`);
	}, [facetValue]);

	// Listen for window resize to determine if small screen
	useEffect(() => {
		function handleResize() {
			setIsSmallScreen(
				window.innerWidth < SMALL_SCREEN_SIZE &&
					window.innerWidth > MOBILE_SCREEN_SIZE
			);
			setIsMobile(window.innerWidth < MOBILE_SCREEN_SIZE);
		}
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const onClick = (idSelected) => {
		setValue(idSelected);
		const trackingObjSelected = trackingObjContainer.find(
			(obj) => obj.id === idSelected
		);
		const { id: ignored, ...trackingObj } = trackingObjSelected;
		trackEvent(widgetName, windowId, trackingObj);
	};

	// onClick handler is set up to reduce lag + address first option select bug
	// There is a warning being thrown when input has no onChange, so readOnly has been used.
	// readOnly will not affect ability to click since the onClick handler assigns the value not the click itself
	return (
		<div className={setClassNames(wrapperClasses)}>
			<ol className={setClassNames(facetsListClasses)}>
				{activityData.map((activity) => (
					<FacetButton
						key={activity.id}
						type={isSmallScreen || isMobile ? 'checkbox' : 'radio'}
						classNames={setClassNames(facetClasses)}
						onClick={onClick}
						facetValue={facetValue}
						activity={activity}
					/>
				))}
			</ol>
		</div>
	);
};

Options.propTypes = {
	hookProps: PropTypes.shape.isRequired
};

export default Options;
